import { useState } from 'react';
import { ChevronDown, Plus, Check } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getPlanDisplayName } from '@/lib/plans';

interface WorkspaceSwitcherProps {
  variant?: 'sidebar' | 'topbar';
}

export function WorkspaceSwitcher({ variant = 'sidebar' }: WorkspaceSwitcherProps) {
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspaceContext();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const generateUniqueSlug = async (baseName: string): Promise<string> => {
    // Clean the name to create a base slug
    let baseSlug = baseName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .substring(0, 30);

    // Ensure minimum length
    if (baseSlug.length < 3) {
      baseSlug = 'espace';
    }

    // Check if slug is unique, if not add a counter
    let finalSlug = baseSlug;
    let counter = 0;

    while (true) {
      const { data } = await supabase
        .from('workspaces')
        .select('id')
        .eq('slug', finalSlug)
        .maybeSingle();

      if (!data) break; // Slug is unique

      counter++;
      finalSlug = `${baseSlug}-${counter}`;
    }

    return finalSlug;
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim() || !user) return;

    setIsCreating(true);
    try {
      // Generate a readable unique slug from the workspace name
      const slug = await generateUniqueSlug(newWorkspaceName.trim());

      // Create workspace
      const { data: workspace, error: workspaceError } = await supabase
        .from('workspaces')
        .insert({
          name: newWorkspaceName.trim(),
          slug,
          support_email: user.email,
        })
        .select()
        .single();

      if (workspaceError) throw workspaceError;

       // Note: Le trigger handle_new_workspace() ajoute automatiquement l'owner

      setIsCreateDialogOpen(false);
      setNewWorkspaceName('');
      
      // Switch to the new workspace immediately
      switchWorkspace(workspace);
      
      toast({
        title: 'Espace créé',
        description: `Vous êtes maintenant dans l'espace "${workspace.name}".`,
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de créer l\'espace.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (!activeWorkspace) return null;

  const isTopbar = variant === 'topbar';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button 
            className={`flex items-center gap-2 rounded-lg transition-colors text-left ${
              isTopbar 
                ? 'px-3 py-2 hover:bg-muted border border-border bg-background' 
                : 'w-full p-2 hover:bg-sidebar-accent'
            }`}
          >
            <Avatar className="h-7 w-7">
              {activeWorkspace.logo_url && (
                <AvatarImage src={activeWorkspace.logo_url} alt={activeWorkspace.name} />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                {activeWorkspace.name.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isTopbar ? 'text-foreground' : 'text-sidebar-foreground'}`}>
                {activeWorkspace.name}
              </p>
            </div>
            <ChevronDown className={`h-4 w-4 shrink-0 ${isTopbar ? 'text-muted-foreground' : 'text-sidebar-muted'}`} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-popover border border-border shadow-lg z-50">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            Mes espaces
          </div>
          {workspaces.map((workspace) => (
            <DropdownMenuItem
              key={workspace.id}
              onClick={() => switchWorkspace(workspace)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Avatar className="h-6 w-6">
                {workspace.logo_url && (
                  <AvatarImage src={workspace.logo_url} alt={workspace.name} />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {workspace.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <span className="truncate block">{workspace.name}</span>
                <span className="text-xs text-muted-foreground">
                  {getPlanDisplayName(workspace.plan)}
                </span>
              </div>
              {workspace.id === activeWorkspace.id && (
                <Check className="h-4 w-4 text-primary shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="text-primary cursor-pointer"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un nouvel espace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un nouvel espace</DialogTitle>
            <DialogDescription>
              Un espace vous permet d'organiser vos tickets et votre équipe séparément.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Nom de l'espace</Label>
              <Input
                id="workspace-name"
                placeholder="Mon entreprise"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateWorkspace();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateWorkspace}
              disabled={!newWorkspaceName.trim() || isCreating}
            >
              {isCreating ? 'Création...' : 'Créer l\'espace'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
